import React, { useMemo } from 'react'

import { SxProps } from '@mui/material'
import { PeriodFilter } from 'types/GlobalWidget'
import { getResourceVariant, TKeyMetric } from 'types/GlobalKeyMetric'
import { TMetricSettings, TMetricQueryParams } from 'types/GlobalMetric'

import { useAttributeOption } from 'redux/hooks/AttributeOptions'

import { Context } from 'types/GlobalUserEvents'
import {
  TChartDataset,
  TChartVariant
} from 'components_new/molecules/charts/types'

import { useTranslation } from 'language/translator'
import { TMetricDataSource, useMetricData } from 'redux/hooks/MetricData'
import { useIsMobile } from 'utils/hooks'

import Box from 'components_new/atoms/Box'
import Icon from 'components_new/atoms/Icon'
import Text from 'components_new/atoms/Text'

import ChangeText from 'components_new/molecules/ChangeText'
import EditableTitle from 'components_new/molecules/EditableTitle'
import GenericListItem from 'components_new/molecules/GenericListItem'
import GroupsChip from 'components_new/molecules/GroupsChip'
import { OptionProps } from 'components_new/molecules/OptionsMenu'
import OptionsMenuButton from 'components_new/molecules/OptionsMenuButton'
import LineChart from 'components_new/molecules/charts/LineChart'
import ResourcesChip from 'components_new/molecules/ResourcesChip'
import ValueText from 'components_new/molecules/ValueText'

import MetricTitle from 'components_new/organisms/MetricTitle'

import Error from './error'
import Loading from './loading'

import {
  getIncreaseIsPositive,
  getLabels,
  getLineColor,
  getMetricDataKey,
  getMetricTitle,
  getValues
} from 'utils/metric'

import { formatValue, getPrefixValue } from '../Widget/Types/utils'
import { AUTO_SIGNIFICANT_FIGURES, getAutoScale } from 'utils/autoFormat'

export interface MetricPreviewProps {
  autoFocusPinComment?: boolean
  border?: boolean
  chartVariant?: TChartVariant
  disableBorderRadius?: boolean
  editMode?: boolean
  height?: number
  horizontalLines?: { value: number; label?: string; color?: string }[]
  id: string
  keyMetric?: TKeyMetric
  linked?: boolean
  onChangePinComment?: (value: string) => void
  onGroups?: () => void
  onResources?: () => void
  onView?: () => void
  options?: OptionProps[]
  queryParams?: TMetricQueryParams
  settings: TMetricSettings
  sx?: SxProps
  titleOverride?: string
}

export const LINKED_METRIC_PREVIEW_HEIGHT = 56
export const METRIC_PREVIEW_HEIGHT = 200

const MetricPreview = React.memo((props: MetricPreviewProps) => {
  const {
    autoFocusPinComment,
    border,
    chartVariant,
    disableBorderRadius,
    editMode,
    height,
    horizontalLines,
    id,
    keyMetric,
    linked,
    onChangePinComment,
    onGroups,
    onResources,
    onView,
    options,
    queryParams,
    settings,
    sx,
    titleOverride
  } = props
  const { lang, t, translateEnum } = useTranslation()
  const isMobile = useIsMobile()

  // The edit flow may pass settings that diverge from the key metric's
  // stored settings; only the stored settings match what the key-metrics
  // endpoint computes, so divergent settings fall back to the metrics
  // endpoint (which has no target series).
  const source: TMetricDataSource = useMemo(
    () =>
      keyMetric &&
      getMetricDataKey(settings) === getMetricDataKey(keyMetric.metric.settings)
        ? { keyMetric }
        : { id, settings },
    [keyMetric, id, settings]
  )

  const {
    metric,
    rawData,
    error: rawDataError
  } = useMetricData(source, queryParams, false)
  const segmentOption = useAttributeOption(rawData?.segment_attribute_option_id)

  if (rawDataError) {
    return <Error editMode={editMode} options={options} />
  }

  if (!metric || !rawData) {
    return <Loading {...props} />
  }

  // controlled values
  const labels = getLabels({ lang, rawData, segmentOption })
  const increaseIsPositive = keyMetric
    ? keyMetric.increase_is_positive
    : getIncreaseIsPositive({ metric })
  const values = getValues({ rawData })

  const nonNullValues = values.filter((v) => v !== null)
  const lastNonNullValue = nonNullValues[nonNullValues.length - 1]

  const title = titleOverride
    ? titleOverride
    : keyMetric
      ? keyMetric.title
      : getMetricTitle(metric, settings, lang, translateEnum)

  const showComment = (keyMetric?.pinned && editMode) || keyMetric?.pin_comment

  const hasBudget =
    !!keyMetric?.metric.settings.variant_kpi_template_id &&
    keyMetric.target_activated
  const targetValues = rawData.target?.map((d) => d.value) ?? null

  // one unit for the value, target and chart, e.g. GWh
  const { unitPrefix, unitLabel } = getAutoScale(
    [...values, ...(targetValues ?? [])],
    metric.unit,
    metric.unit_prefixes
  )
  const scale = (value: number | null) => getPrefixValue(value, unitPrefix)

  const resolvedTargetValue =
    targetValues?.filter((v) => v !== null).pop() ?? null

  // datasets
  const budgetDataset: TChartDataset = {
    color: '#1a1a1d', // @TODO
    label: t('key_metrics', hasBudget ? 'budget' : 'target'),
    order: 1,
    style: 'dashed' as const,
    type: 'line' as const,
    unit: unitLabel,
    values: (targetValues ?? []).map(scale)
  }
  const valueDataset: TChartDataset = {
    color: getLineColor({
      increaseIsPositive: increaseIsPositive,
      values: values
    }),
    label: title,
    main: true,
    order: 2,
    type: 'line',
    unit: unitLabel,
    values: values.map(scale)
  }
  const showTarget =
    keyMetric && keyMetric.target_activated && resolvedTargetValue !== null
  const datasets: TChartDataset[] = [
    valueDataset,
    ...(showTarget ? [budgetDataset] : [])
  ]

  /*-- linked metric preview --*/
  if (linked)
    return (
      <Box
        sx={{
          alignItems: 'center',
          bgcolor: 'background.default',
          ...(border ? { border: '1px solid', borderColor: 'divider' } : null),
          borderRadius: disableBorderRadius ? 0 : 1,
          display: 'flex',
          gap: 1,
          height: LINKED_METRIC_PREVIEW_HEIGHT,
          justifyContent: 'space-between',
          p: 1,
          ...sx
        }}
      >
        <GenericListItem
          component="div"
          disablePadding={true}
          body={`${formatValue(
            lastNonNullValue,
            unitPrefix,
            1,
            undefined,
            undefined,
            AUTO_SIGNIFICANT_FIGURES
          )}${` ${unitLabel || t('global', 'pcs')}`}`}
          icon={{
            color: 'disabled',
            fontSize: 'small',
            name: 'Link'
          }}
          title={title}
          TitleProps={{ variant: 'body1' }}
          sx={{ width: 'fit-content' }}
        />

        <Box
          sx={{
            flex: 1,
            height: 32,
            minWidth: 0,
            maxWidth: 96
          }}
        >
          <LineChart
            data={{
              labels: labels,
              isTimeseries: true,
              datasets: [
                {
                  color: getLineColor({
                    increaseIsPositive: increaseIsPositive,
                    values: values
                  }),
                  label: '',
                  type: 'line',
                  unit: unitLabel,
                  values: values.map(scale)
                }
              ]
            }}
            significantFigures={AUTO_SIGNIFICANT_FIGURES}
            variant="minimal"
          />
        </Box>
      </Box>
    )

  /*-- standard metric preview --*/
  return (
    <Box
      sx={{
        borderRadius: disableBorderRadius ? undefined : 1,
        outline: editMode ? '1px solid' : undefined,
        outlineColor: 'primary',
        ...sx
      }}
    >
      <Box
        sx={{
          bgcolor: 'background.default',
          borderRadius: disableBorderRadius ? 0 : 1,
          borderBottomLeftRadius: showComment ? 0 : undefined,
          borderBottomRightRadius: showComment ? 0 : undefined,
          display: 'flex',
          flexDirection: 'column',
          height: height ?? METRIC_PREVIEW_HEIGHT,
          p: 2,
          position: 'relative',
          ['&:hover']: {
            ['& .MetricTitleInfoButton']: {
              opacity: 1
            }
          }
        }}
      >
        <Box
          sx={{
            alignItems: 'flex-start',
            display: 'flex',
            gap: 1,
            height: 28,
            justifyContent: 'space-between',
            mb: 1,
            minWidth: 0,
            position: 'relative',
            width: '100%'
          }}
        >
          <MetricTitle
            context={Context.FROM_METRIC_PREVIEW}
            id={id}
            keyMetric={keyMetric}
            link={true}
            settings={settings}
            size="small"
            sx={{ mt: -0.5 }}
            titleOverride={titleOverride}
          />

          <Box>
            {editMode && options ? (
              <OptionsMenuButton
                color="default"
                edge="end"
                options={options}
                size="small"
                sx={{ mt: -0.5 }}
              />
            ) : null}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flex: 1,
            gap: 2,
            minHeight: 0,
            minWidth: 0,
            width: '100%'
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                gap: 0.25,
                mt: -1,
                ml: -0.5
              }}
            >
              {keyMetric ? (
                <>
                  {onGroups ? (
                    <GroupsChip groups={keyMetric.groups} onClick={onGroups} />
                  ) : null}
                  {onResources ? (
                    <ResourcesChip
                      resources={keyMetric.resources.map((r) => ({
                        id: r.id,
                        title: r.title,
                        variant: getResourceVariant(r)
                      }))}
                      onClick={onResources}
                    />
                  ) : null}
                </>
              ) : null}
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'flex-end'
              }}
            >
              <Text
                color="text.primary"
                gutterBottom={true}
                noWrap={true}
                variant={'h3'}
              >
                {formatValue(
                  lastNonNullValue,
                  unitPrefix,
                  1,
                  undefined,
                  undefined,
                  AUTO_SIGNIFICANT_FIGURES
                )}{' '}
                {unitLabel}
              </Text>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}
              >
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {/*-- target --*/}
                  {showTarget ? (
                    <ValueText
                      label={t('key_metrics', hasBudget ? 'budget' : 'target')}
                      unit={unitLabel}
                      value={scale(resolvedTargetValue)}
                    />
                  ) : null}

                  {/*-- 12m change --*/}
                  <ChangeText
                    denominator={values[0]}
                    increaseIsPositive={increaseIsPositive}
                    label={
                      translateEnum(
                        'period_filters',
                        PeriodFilter.LAST_12_MONTHS_TO_TODAY
                      ).short
                    }
                    numerator={values[values.length - 1]}
                    sx={{ flexBasis: isMobile ? '25%' : '15%' }}
                    unit={metric.unit}
                    unitPrefixes={metric.unit_prefixes}
                  />

                  {/*-- 1m change --*/}
                  <ChangeText
                    denominator={values[values.length - 2]}
                    increaseIsPositive={increaseIsPositive}
                    label={labels.at(-1) ?? ''}
                    numerator={values[values.length - 1]}
                    sx={{ flexBasis: isMobile ? '25%' : '15%' }}
                    unit={metric.unit}
                    unitPrefixes={metric.unit_prefixes}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            onClick={onView}
            sx={{
              cursor: onView ? 'zoom-in' : undefined,
              flex: 1,
              minHeight: 0,
              minWidth: 0,
              mb: -0.1,
              position: 'relative'
            }}
          >
            <LineChart
              beginYAtZero={showTarget}
              data={{
                datasets: datasets,
                horizontalLines: horizontalLines?.map((line) => ({
                  ...line,
                  value: scale(line.value) ?? line.value
                })),
                labels: labels,
                isTimeseries: true
              }}
              significantFigures={AUTO_SIGNIFICANT_FIGURES}
              variant={chartVariant ?? 'standard'}
            />
          </Box>
        </Box>
      </Box>

      {/*-- pinned comment --*/}
      {showComment ? (
        <Box
          sx={{
            alignItems: 'center',
            border: '1px solid',
            borderColor: 'divider',
            borderBottomLeftRadius: '8px',
            borderBottomRightRadius: '8px',
            borderTop: 0,
            display: 'flex',
            gap: 1,
            p: 2
          }}
        >
          <Icon color="disabled" fontSize="small" name="Comment" />
          <Box sx={{ flex: 1 }}>
            <EditableTitle
              actions={true}
              autoFocus={autoFocusPinComment}
              disabled={!editMode}
              onSubmit={(value) => {
                if (!onChangePinComment) return

                onChangePinComment(value ?? '')
              }}
              placeholder={t('key_metrics', 'pin_comment_placeholder')}
              value={keyMetric.pin_comment}
              variant="body1"
              sx={{ width: '100%' }}
            />
          </Box>
        </Box>
      ) : null}
    </Box>
  )
})

export default MetricPreview
